import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { get, isEmpty } from 'lodash';

import { SagisConfig } from 'containers/AppContainer/typings';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import {
  VALIDATION_DECISION,
  EMPTY_VALIDATION_DECISION,
} from 'containers/data-structure';
import {
  SCALE_CASES_KEY,
  MANAGEMENT_MODES,
} from 'containers/RecapManagerPage/constants';
import validate, { formsAreValidated } from 'containers/services/validateForm';
import { commonRightValidationsValidator } from 'containers/RecapManagerPage/CommonRightValidationForm';

export const foreignValidationConstraints = (
  decision: any,
  isChangeNatureEnabled: boolean | undefined,
  isConfirmationPreopeningEnabled: boolean | undefined,
) => {
  return {
    [VALIDATION_DECISION.CIRCUMSTANCE]: {
      presence:
        get(decision, [VALIDATION_DECISION.SCALE_CASE], '') ===
          SCALE_CASES_KEY.OTHER &&
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
          MANAGEMENT_MODES.FOREIGN,
    },
    [VALIDATION_DECISION.RESPONSIBILITY]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.FOREIGN,
    },
    [VALIDATION_DECISION.CIRCUMSTANCE_CODE]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.FOREIGN,
    },
    [VALIDATION_DECISION.SCALE_CASE]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.FOREIGN,
    },
    [VALIDATION_DECISION.MANAGEMENT_MODE]: {
      presence: true,
    },
    [VALIDATION_DECISION.CHANGE_NATURE]: {
      presence: isChangeNatureEnabled,
    },
    [VALIDATION_DECISION.CONFIRMATION_PREOPENING]: {
      presence: isConfirmationPreopeningEnabled,
    },
  };
};

export const foreignValidationValidator = (
  decision: any,
  isChangeNatureEnabled: boolean,
  isConfirmationPreopeningEnabled: boolean,
) => {
  return validate(
    decision,
    foreignValidationConstraints(
      decision,
      isChangeNatureEnabled,
      isConfirmationPreopeningEnabled,
    ),
  );
};
export interface ForeignValidationFormProps {
  intl: IntlShape;
  scaleCases: Array<SagisConfig>;
  decision: any;
  updateDecision: Function;
  circumstances: Array<SagisConfig>;
  responsabilities: Array<SagisConfig>;
  updateDecisionValidation: Function;
  isChangeNatureEnabled: boolean;
  isConfirmationPreopeningEnabled: boolean;
}
const Container = styled.div`
  &&.root {
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  }
  .input {
    width: 327px;
    margin-bottom: 33px;
    margin-left: 24px;
    margin-right: 24px;
  }
  .label {
    font-size: 13px;
    font-family: Roboto;
  }
  .MuiFormLabel-root {
    font-size: 13px;
    color: #4f4f4f;
  }
  .MuiInputLabel-shrink {
    color: #00000061;
  }
  .MuiFormLabel-root.Mui-focused {
    color: rgba(9, 122, 194, 1);
  }
`;

const ForeignValidationForm: React.FC<ForeignValidationFormProps> = (
  props,
): JSX.Element => {
  useEffect(() => {
    props.updateDecision({
      ...EMPTY_VALIDATION_DECISION,
      [VALIDATION_DECISION.MANAGEMENT_MODE]:
        props.decision[VALIDATION_DECISION.MANAGEMENT_MODE],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.decision[VALIDATION_DECISION.MANAGEMENT_MODE]]);

  const [isScaleCaseOther, updateIsScaleCaseOther] = useState(false);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    let expectedDecision: any = { ...props.decision };
    switch (field) {
      case VALIDATION_DECISION.SCALE_CASE: {
        expectedDecision[field] = value;
        expectedDecision[VALIDATION_DECISION.CIRCUMSTANCE_CODE] = '';
        expectedDecision[VALIDATION_DECISION.RESPONSIBILITY] = '';
        if (value === SCALE_CASES_KEY.OTHER) {
          updateIsScaleCaseOther(true);
        } else {
          updateIsScaleCaseOther(false);
        }
        break;
      }
      case VALIDATION_DECISION.CIRCUMSTANCE_CODE: {
        expectedDecision[field] = value;
        const expectedCircumstance = props.circumstances.find(
          circumstance => circumstance.ItemKey === value,
        );
        expectedDecision[
          VALIDATION_DECISION.RESPONSIBILITY
        ] = expectedCircumstance
          ? expectedCircumstance.responsablityRateCode
          : '';
        if (isScaleCaseOther) {
          expectedDecision[
            VALIDATION_DECISION.CIRCUMSTANCE
          ] = expectedCircumstance ? expectedCircumstance.ItemDescription : '';
        }
        break;
      }
      default:
        expectedDecision[field] = value;
    }
    let expectedDecisionValidation = {};
    if (
      props.decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
      MANAGEMENT_MODES.FOREIGN
    ) {
      expectedDecisionValidation =
        foreignValidationValidator(
          expectedDecision,
          props.isChangeNatureEnabled,
          props.isConfirmationPreopeningEnabled,
        ) || {};
    } else {
      expectedDecisionValidation =
        commonRightValidationsValidator(
          expectedDecision,
          props.isChangeNatureEnabled,
          props.isConfirmationPreopeningEnabled,
        ) || {};
    }

    props.updateDecision(expectedDecision);
    props.updateDecisionValidation(
      formsAreValidated(expectedDecisionValidation),
    );
  };

  const getCircumstances = () => {
    let circumstances = props.circumstances.filter(
      circumstance =>
        circumstance.ItemGroup ===
        props.decision[VALIDATION_DECISION.SCALE_CASE],
    );

    return circumstances;
  };
  const inputLabel = React.useRef<HTMLLabelElement>(null);
  return (
    <Container>
      <div className="fields">
        <FormControl className="input">
          <InputLabel ref={inputLabel} id="scaleCase" className="label">
            {translate({ ...messages.scaleCase }, props)}
          </InputLabel>
          <Select
            name={VALIDATION_DECISION.SCALE_CASE}
            value={get(props.decision, [VALIDATION_DECISION.SCALE_CASE])}
            onChange={handleChange(VALIDATION_DECISION.SCALE_CASE)}
          >
            {!isEmpty(props.scaleCases) &&
              props.scaleCases.map(scaleCase => (
                <MenuItem value={scaleCase.ItemKey} key={scaleCase.ItemKey}>
                  {scaleCase.ItemDescription}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {get(props.decision, [VALIDATION_DECISION.SCALE_CASE]) && (
          <FormControl className="input">
            <InputLabel
              ref={inputLabel}
              id="circumstanceCode"
              className="label"
            >
              {translate({ ...messages.circumstance }, props)}
            </InputLabel>
            <Select
              name={VALIDATION_DECISION.CIRCUMSTANCE_CODE}
              value={get(props.decision, [
                VALIDATION_DECISION.CIRCUMSTANCE_CODE,
              ])}
              onChange={handleChange(VALIDATION_DECISION.CIRCUMSTANCE_CODE)}
            >
              {!isEmpty(getCircumstances()) &&
                getCircumstances().map(circumstance => (
                  <MenuItem
                    value={circumstance.ItemKey}
                    key={circumstance.ItemKey}
                  >
                    {circumstance.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {isScaleCaseOther &&
          get(props.decision, [VALIDATION_DECISION.CIRCUMSTANCE_CODE]) && (
            <TextField
              onChange={handleChange(VALIDATION_DECISION.CIRCUMSTANCE)}
              name={VALIDATION_DECISION.CIRCUMSTANCE}
              value={get(props.decision, [VALIDATION_DECISION.CIRCUMSTANCE])}
              label={translate({ ...messages.circumstanceDetail }, props)}
              className="input"
              multiline
            />
          )}
        {get(props.decision, [VALIDATION_DECISION.CIRCUMSTANCE_CODE]) && (
          <FormControl className="input">
            <InputLabel ref={inputLabel} id="responsibility" className="label">
              {translate({ ...messages.responsibility }, props)}
            </InputLabel>
            <Select
              name={VALIDATION_DECISION.RESPONSIBILITY}
              value={get(props.decision, [VALIDATION_DECISION.RESPONSIBILITY])}
              onChange={handleChange(VALIDATION_DECISION.RESPONSIBILITY)}
              disabled={!isScaleCaseOther}
            >
              {!isEmpty(props.responsabilities) &&
                props.responsabilities.map(responsability => (
                  <MenuItem
                    value={responsability.ItemKey}
                    key={responsability.ItemKey}
                  >
                    {responsability.ItemDescription}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {get(props.decision, [VALIDATION_DECISION.RESPONSIBILITY]) && (
          <TextField
            onChange={handleChange(VALIDATION_DECISION.COMMENT)}
            name={VALIDATION_DECISION.COMMENT}
            value={get(props.decision, [VALIDATION_DECISION.COMMENT])}
            label={translate({ ...messages.comment }, props)}
            className="input"
            multiline
          />
        )}
      </div>
    </Container>
  );
};

export default injectIntl(ForeignValidationForm);
